import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Radio, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { usePermissions } from '@hooks/usePermissions';
import { vehicleService } from '@services/vehicleService';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import { VehicleFormData } from '@/types/vehicle';
import type { VehicleTracker, VehicleTrackerFormData } from '@/types/vehicleTracker';
import { formatAgo } from '@pages/fleetTracking/trackerUtils';

/** Campos do cadastro do rastreador para o PUT /trackers/:id, trocando só o veículo. */
const trackerPayload = (t: VehicleTracker, vehicleId: number | undefined): VehicleTrackerFormData => ({
  device_id: t.device_id,
  imei: t.imei,
  model: t.model,
  sim_number: t.sim_number,
  vehicle_id: vehicleId,
  is_active: t.is_active,
  notes: t.notes,
});

export const VehicleFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  // diretoria/liberação e vínculo do rastreador: só o admin (fleet_tracking.manage)
  const { checkPermission } = usePermissions();
  const canManageFleet = checkPermission('fleet_tracking.manage').hasPermission;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<VehicleFormData>({
    description: '',
    license_plate: '',
    renavan: '',
    tracker_imei: '',
    is_active: true,
    fiscal_state: '',
    manufacturer: '',
    is_executive: false,
    delivery_enabled: true,
  });

  // rastreadores cadastrados, para escolher o deste veículo
  const [trackers, setTrackers] = useState<VehicleTracker[]>([]);
  const [trackerId, setTrackerId] = useState<number | ''>('');
  const linkedTracker = trackers.find((t) => isEditing && t.vehicle_id === Number(id) && t.is_active);

  useEffect(() => {
    if (!canManageFleet) return;
    vehicleTrackerService
      .list()
      .then((list) => {
        setTrackers(list);
        const current = list.find((t) => isEditing && t.vehicle_id === Number(id) && t.is_active);
        setTrackerId(current ? current.vehicle_tracker_id : '');
      })
      .catch(() => setTrackers([]));
  }, [canManageFleet, id, isEditing]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && id) {
      loadVehicle(id);
    }
  }, [id, isEditing]);

  const loadVehicle = async (vehicleId: string) => {
    try {
      setLoading(true);
      const vehicle = await vehicleService.getById(parseInt(vehicleId));
      setFormData({
        description: vehicle.description,
        license_plate: vehicle.license_plate,
        renavan: vehicle.renavan || '',
        tracker_imei: vehicle.tracker_imei || '',
        is_active: vehicle.is_active,
        fiscal_state: vehicle.fiscal_state || '',
        manufacturer: vehicle.manufacturer || '',
        is_executive: vehicle.is_executive,
        delivery_enabled: vehicle.delivery_enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar veículo');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.description.trim()) newErrors.description = 'Descrição é obrigatória';
    if (!formData.license_plate.trim()) newErrors.license_plate = 'Placa é obrigatória';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      const payload: VehicleFormData = canManageFleet
        ? formData
        : { ...formData, is_executive: undefined, delivery_enabled: undefined };
      const saved = isEditing && id ? await vehicleService.update(parseInt(id), payload) : await vehicleService.create(payload);
      if (canManageFleet) await saveTrackerLink(saved.vehicle_id);
      navigate('/settings/transport/vehicles');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  // Vincula/desvincula o rastreador pelo cadastro de rastreadores (mesma regra
  // da tela Rastreadores: um rastreador ativo por veículo).
  const saveTrackerLink = async (vehicleId: number) => {
    const previous = trackers.find((t) => t.vehicle_id === vehicleId && t.is_active);
    const chosen = trackers.find((t) => t.vehicle_tracker_id === trackerId);
    if (previous && previous.vehicle_tracker_id !== chosen?.vehicle_tracker_id) {
      await vehicleTrackerService.update(previous.vehicle_tracker_id, trackerPayload(previous, undefined));
    }
    if (chosen && chosen.vehicle_id !== vehicleId) {
      await vehicleTrackerService.update(chosen.vehicle_tracker_id, { ...trackerPayload(chosen, vehicleId), is_active: true });
    }
  };

  const handleChange = (field: keyof VehicleFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/settings/transport/vehicles')} icon={<ArrowLeft size={20} />} />
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Veículo' : 'Novo Veículo'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Atualize os dados do veículo' : 'Preencha os dados para cadastrar um novo veículo'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <Card>
        <CardHeader title="Dados do Veículo" />
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Descrição *</label>
            <Input
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Ex: Fiorino Branca 2022"
              className={errors.description ? 'border-red-500' : ''}
            />
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Placa *</label>
            <Input
              value={formData.license_plate}
              onChange={(e) => handleChange('license_plate', e.target.value.toUpperCase())}
              placeholder="ABC-1234"
              className={errors.license_plate ? 'border-red-500' : ''}
            />
            {errors.license_plate && <p className="text-red-500 text-sm mt-1">{errors.license_plate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Renavan</label>
            <Input
              value={formData.renavan || ''}
              onChange={(e) => handleChange('renavan', e.target.value)}
              placeholder="Código Renavan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">IMEI do Rastreador</label>
            <Input
              value={formData.tracker_imei || ''}
              onChange={(e) => handleChange('tracker_imei', e.target.value)}
              placeholder="IMEI do dispositivo rastreador"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fabricante</label>
            <Input
              value={formData.manufacturer || ''}
              onChange={(e) => handleChange('manufacturer', e.target.value)}
              placeholder="Ex: Fiat, Volkswagen, Mercedes-Benz"
              maxLength={50}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">UF Fiscal</label>
            <Input
              value={formData.fiscal_state || ''}
              onChange={(e) => handleChange('fiscal_state', e.target.value.toUpperCase())}
              placeholder="Ex: SP"
              maxLength={2}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Ativo</label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Uso do veículo" />
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="is_executive"
              checked={!!formData.is_executive}
              disabled={!canManageFleet}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_executive: e.target.checked,
                  // marcar como da diretoria começa sem liberação para entregas
                  delivery_enabled: e.target.checked ? false : true,
                }))
              }
              className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="is_executive" className="text-sm text-slate-700">
              <span className="font-medium">Veículo da diretoria</span>
              <br />
              <span className="text-slate-500">
                Não aparece na escolha de veículo das rotas e entregas; é acompanhado só no Mapa da Frota.
              </span>
            </label>
          </div>
          {formData.is_executive && (
            <div className="flex items-start gap-2 ml-6">
              <input
                type="checkbox"
                id="delivery_enabled"
                checked={!!formData.delivery_enabled}
                disabled={!canManageFleet}
                onChange={(e) => handleChange('delivery_enabled', e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label htmlFor="delivery_enabled" className="text-sm text-slate-700">
                <span className="font-medium">Liberado para entregas</span>
                <br />
                <span className="text-slate-500">Enquanto liberado, pode ser escolhido nas rotas. Desmarque quando ele voltar à diretoria.</span>
              </label>
            </div>
          )}
          {!canManageFleet && <p className="text-xs text-slate-500">Só o administrador altera estas opções.</p>}
        </CardContent>
      </Card>

      {canManageFleet && (
        <Card>
          <CardHeader title={<span className="flex items-center gap-2"><Radio size={18} /> Rastreador GPS</span>} />
          <CardContent className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rastreador deste veículo</label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                value={trackerId}
                onChange={(e) => setTrackerId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Sem rastreador</option>
                {trackers
                  .filter((t) => t.is_active || t.vehicle_tracker_id === trackerId)
                  .map((t) => (
                    <option key={t.vehicle_tracker_id} value={t.vehicle_tracker_id}>
                      {t.device_id}
                      {t.model ? ` — ${t.model}` : ''}
                      {t.vehicle_id && t.vehicle_id !== Number(id) ? ` (hoje em ${t.vehicle_label ?? `veículo #${t.vehicle_id}`})` : ''}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Escolher um rastreador que está em outro veículo o transfere para este. O aparelho precisa estar cadastrado em{' '}
                <Link to="/settings/transport/trackers" className="text-blue-600 hover:underline">
                  Configuração → Transporte → Rastreadores
                </Link>{' '}
                (o ID aparece lá em "Aparelhos não cadastrados").
              </p>
            </div>
            {linkedTracker && (
              <p className="text-sm text-slate-600">
                Última comunicação {formatAgo(linkedTracker.last_seen_seconds_ago)}
                {linkedTracker.last_battery_percent != null ? ` · bateria ${linkedTracker.last_battery_percent}%` : ''}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate('/settings/transport/vehicles')}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={18} />}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
};
